import {useMemo, useState} from 'react';
import {view} from '@sail/ui';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';
import {useSelector, setValue} from 'src/v2/state/store';
import {Text} from 'src/v2/components/common/Text';
import {BrandedButton} from 'src/v2/components/common/BrandedButton';
import {SelectionCard} from 'src/v2/components/common/SelectionCard';
import {TOKEN_INFO_MAP} from 'src/v2/components/tokenSelect/tokenSelectConstants';
import {getCurrencyIcon} from 'src/v2/util/getCurrencyIcon';
import {formatAssetCode} from 'src/v2/util/formatAssetCode';
import {TOKEN_NETWORKS} from 'src/components/Wallet/constants';
import type {OpenapiPostV1CryptoInternalRelayQuoteInputInputCurrencyEnum} from '@stripe-internal/data-v1/schema/types';

const messages = defineMessages({
  selectToken: {
    id: 'crypto.payins.v2.token_select.select_token',
    description: 'Label for token selection list',
    defaultMessage: 'Select token',
  },
  continueButton: {
    id: 'crypto.payins.v2.token_select.continue',
    description: 'Continue button on token selection screen',
    defaultMessage: 'Continue',
  },
});

export const TokenSelectScreen = () => {
  const intl = useIntl();
  const initialToken = useSelector(
    (state) => state.anyCryptoDeposit.selectedToken,
  );
  const supportedCurrencies = useSelector((state) => state.supportedCurrencies);

  const tokenList = useMemo(() => {
    const uniqueTokens = [
      ...new Set(supportedCurrencies.map((c) => c.assetCode)),
    ];
    return uniqueTokens.filter(
      (
        token,
      ): token is OpenapiPostV1CryptoInternalRelayQuoteInputInputCurrencyEnum =>
        token in TOKEN_INFO_MAP,
    );
  }, [supportedCurrencies]);

  const [selectedToken, setSelectedToken] =
    useState<OpenapiPostV1CryptoInternalRelayQuoteInputInputCurrencyEnum | null>(
      initialToken ?? tokenList[0] ?? null,
    );

  const handleContinue = () => {
    if (!selectedToken) {
      return;
    }

    const networks = TOKEN_NETWORKS[selectedToken];

    if (networks.length > 1) {
      setValue((state) => {
        state.anyCryptoDeposit.selectedToken = selectedToken;
        state.anyCryptoDeposit.selectedNetwork = null;
        state.screen = 'ANY_CRYPTO_NETWORK_SELECT';
      });
    } else {
      setValue((state) => {
        state.anyCryptoDeposit.selectedToken = selectedToken;
        const [network] = networks;
        state.anyCryptoDeposit.selectedNetwork = network;
        state.anyCryptoDeposit.quote = {
          depositAddress: null,
          inputAmount: null,
          inputChain: null,
          inputCurrency: null,
        };
        state.screen = 'REFUND';
      });
    }
  };

  return (
    <view.div css={{stack: 'y', gap: 'large'}}>
      <view.div css={{stack: 'y', gap: 'small'}}>
        <Text type="bodyEmphasized">
          <FormattedMessage {...messages.selectToken} />
        </Text>
        <view.div css={{stack: 'y', gap: 'small'}} role="listbox">
          {tokenList.map((token) => {
            const {displayName, processingTimeMessage} = TOKEN_INFO_MAP[token];
            return (
              <SelectionCard
                key={token}
                icon={getCurrencyIcon(token)}
                iconBorderRadius="50%"
                label={formatAssetCode(token)}
                sublabel={displayName}
                detail={intl.formatMessage(processingTimeMessage)}
                selected={selectedToken === token}
                onSelect={() => setSelectedToken(token)}
              />
            );
          })}
        </view.div>
      </view.div>
      <BrandedButton onClick={handleContinue} disabled={!selectedToken}>
        <FormattedMessage {...messages.continueButton} />
      </BrandedButton>
    </view.div>
  );
};
